import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { catchError, map, concatMap, filter } from 'rxjs/operators';
import { of } from 'rxjs';
import * as TenantActions from './tenant.actions';
import { TenantApiService } from './tenant.api.service';
import { InfoService } from '../info-store/info.service';
import { selectSelectedTenantId } from './tenant.selectors';
import { Store } from '@ngrx/store';

const SELECTED_TENANT = 'selected-tenant';

@Injectable()
export class TenantEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private tenantsApi = inject(TenantApiService);
  private infoService = inject(InfoService);

  loadPersonalTenants$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TenantActions.loadPersonalTenants),
      concatMap(() =>
        this.tenantsApi.loadPersonalTenants().pipe(
          map((tenants) => TenantActions.loadTenantsSuccess({ tenants })),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$tenant.error-loading-tenants',
              severity: 'error',
              error,
            });
            //TODO: temporal (hasta api/tenant back)
            localStorage.setItem(SELECTED_TENANT, '0');
            return of(TenantActions.loadTenantsFailure({ error }));
          })
        )
      )
    );
  });

  loadTenantsSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TenantActions.loadTenantsSuccess),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId)),
      filter(([{ tenants }, tenantId]) => tenants?.length > 0 && tenantId === null),
      map(([{ tenants }]) => TenantActions.selectTenant({ tenantId: tenants[0].id }))
    );
  });

  loadSelectedTenant$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TenantActions.loadSelectedTenant),
      map(() => {
        try {
          const tenantId = localStorage.getItem(SELECTED_TENANT);
          return TenantActions.selectTenant({ tenantId });
        } catch (error) {
          return TenantActions.loadSelectedTenantFailure({ error });
        }
      })
    );
  });

  selectTenant$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TenantActions.selectTenant),
      map(({ tenantId }) => {
        try {
          if (tenantId !== null) {
            localStorage.setItem(SELECTED_TENANT, `${tenantId}`);
          } else {
            localStorage.removeItem(SELECTED_TENANT);
          }
          return TenantActions.selectTenantSuccess({ tenantId });
        } catch (error) {
          return TenantActions.selectTenantFailure({ error });
        }
      })
    );
  });
}
